// ═══════════════════════════════════════════════════════════════════
// EXPERIENCE DATA - Vollständiger Werdegang (basierend auf Briefing)
// ═══════════════════════════════════════════════════════════════════

export interface ExperienceExtended {
  id: string;
  company: string;
  role: string;
  period: string;
  startYear: number;
  endYear?: number;
  description: string;
  longDescription?: string;
  highlights: string[];
  technologies: string[];
  industry: string;
  type: 'fulltime' | 'freelance' | 'agency' | 'project';
  current: boolean;
  logo?: string;
  url?: string;
  location?: string;
}

export const experiencesExtended: ExperienceExtended[] = [
  // ═══════════════════════════════════════════════════════════════════
  // AKTUELLE POSITIONEN
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'systempiloten',
    company: 'Systempiloten GmbH & Co. KG',
    role: 'Senior Fullstack Entwickler & UX Architekt',
    period: '11/2023 – Heute',
    startYear: 2023,
    description:
      'Entwicklung eines innovativen KI-gestützten CMS für Remote-Produktion und Medienworkflows.',
    longDescription:
      'Entwicklung einer modularen Plattform für ein KI-gestütztes Content-Management-System mit integriertem Webbuilder. Das System revolutioniert Remote-Produktionsworkflows durch hybride Cloud/On-Premises-Lösungen, Live-Logging und Low-Latency-Livestreaming. Verantwortung für Architekturentscheidungen, Frontend- und Backend-Entwicklung, UX-Konzeption, Performance-Optimierung sowie technische Qualitätssicherung. Aufbau skalierbarer Komponenten-Architekturen, State-Management-Konzepte und Designsysteme.',
    highlights: [
      'KI-gestütztes CMS für Medienproduktion',
      'Hybrid Cloud/On-Premises Workflows',
      'Architekturentscheidungen & Code-Reviews',
      'Performance-Optimierung',
    ],
    technologies: ['React', 'Vue.js', 'Next.js', 'TypeScript', 'Node.js', 'Tailwind CSS', 'KI-Integration'],
    industry: 'Media & Software',
    type: 'fulltime',
    current: true,
    location: 'Remote / Köln',
  },
  {
    id: 'nk-design',
    company: 'NK Design',
    role: 'Geschäftsführer, Lead Developer & UX/UI Designer',
    period: '03/2018 – Heute',
    startYear: 2018,
    description:
      'Eigene Agentur für Webentwicklung und digitale Lösungen. Full-Service von Konzeption bis Deployment.',
    longDescription:
      'Aufbau und Führung einer eigenen Agentur für digitale Produkte. Ganzheitliche Projektverantwortung von Strategie, UX-Konzeption und visuellem Design bis zur technischen Umsetzung, Deployment und Wartung. Entwicklung individueller Weblösungen, E-Commerce-Systeme und Unternehmensplattformen. Direkte Kundenberatung, Anforderungsanalyse, Prototyping, Usability-Tests und kontinuierliche Optimierung.',
    highlights: [
      '50+ erfolgreiche Projekte',
      'Full-Service Agentur',
      'DAX-Konzern Kunden',
      'E-Commerce & Web-Apps',
    ],
    technologies: ['React', 'Vue.js', 'Next.js', 'WordPress', 'Node.js', 'TypeScript', 'PHP'],
    industry: 'Agentur',
    type: 'agency',
    current: true,
    url: 'design-nk.de',
    location: 'Köln',
  },
  {
    id: 'scholz-friends',
    company: 'Scholz & Friends',
    role: 'Full Stack Developer & UX Partner',
    period: '10/2021 – Heute',
    startYear: 2021,
    description:
      'Entwicklung komplexer WordPress-Plattformen für eine der größten deutschen Kreativagenturen (Teil von VML/WPP).',
    longDescription:
      'Entwicklung und Weiterentwicklung komplexer WordPress-Plattformen für die Apothekenbranche (Procter & Gamble) bei Scholz & Friends – einer der führenden Kreativagenturen Deutschlands mit ca. 1.300 Mitarbeitern an 8 Standorten (Teil von VML/WPP). Implementierung neuer Features, Bugfixing, Performance-Optimierung und technische Qualitätssicherung. Enge Abstimmung mit Designteams zur sauberen Umsetzung von UX-Konzepten und responsiven Oberflächen.',
    highlights: [
      'Procter & Gamble Projekt',
      'Vue.js & WordPress Integration',
      'Performance-Optimierung',
      'Teil von VML/WPP Netzwerk',
    ],
    technologies: ['PHP', 'MySQL', 'Vue.js', 'Tailwind CSS', 'WordPress', 'WooCommerce'],
    industry: 'Kreativagentur',
    type: 'freelance',
    current: true,
    location: 'Remote',
  },

  // ═══════════════════════════════════════════════════════════════════
  // ABGESCHLOSSENE POSITIONEN
  // ═══════════════════════════════════════════════════════════════════
  {
    id: 'mercedes',
    company: 'Mercedes-Benz AG',
    role: 'Technical Project Manager E-Commerce',
    period: '11/2022 – 04/2023',
    startYear: 2022,
    endYear: 2023,
    description:
      'Steuerung und technischer Rollout eines neuen internationalen Online-Shops.',
    longDescription:
      'Steuerung und technischer Rollout eines neuen internationalen Online-Shops. Verantwortung für API-Schnittstellenmanagement, Koordination interdisziplinärer Teams, Release-Planung, Qualitätssicherung sowie Budget- und Termincontrolling. Enge Zusammenarbeit mit Design- und Entwicklungsteams zur Optimierung der User Experience im Frontend und Stabilität der Systemlandschaft.',
    highlights: [
      'Online-Shop Rollout in 5 Märkte',
      'API & SAP Commerce Integration',
      'Internationale Teams koordiniert',
      'Agile/Scrum Prozesse',
    ],
    technologies: ['REST APIs', 'SAP Commerce', 'Jira', 'Confluence', 'Agile/Scrum'],
    industry: 'Automotive',
    type: 'freelance',
    current: false,
    location: 'Stuttgart / Remote',
  },
  {
    id: 'positec',
    company: 'Positec Germany GmbH',
    role: 'Fullstack Developer, UX/UI Designer & Tech Lead',
    period: '10/2021 – 02/2023',
    startYear: 2021,
    endYear: 2023,
    description:
      'Entwicklung für den weltweit führenden Hersteller von Elektrowerkzeugen und Gartengeräten (WORX, Landroid).',
    longDescription:
      'Verantwortung für Entwicklung, UX/UI-Design und technische Leitung von acht internationalen Unternehmenswebsites sowie einer mobilen App für Positec – den Hersteller der Marken WORX und Landroid, dem meistverkauften Mähroboter Europas. Koordination von Releases, Feature-Entwicklung, Bugfixing und Performance-Optimierung. Gestaltung konsistenter Benutzeroberflächen, Aufbau wiederverwendbarer Komponenten und Sicherstellung einer hohen Codequalität.',
    highlights: [
      '8 internationale WORX-Websites',
      'Landroid Mobile App',
      'Tech Lead Position',
      'Europas #1 Mähroboter-Marke',
    ],
    technologies: ['PHP', 'JavaScript', 'WordPress', 'Kotlin', 'Swift', 'MySQL', 'REST APIs'],
    industry: 'Consumer Electronics',
    type: 'freelance',
    current: false,
    location: 'Remote',
  },
  {
    id: 'valsight',
    company: 'Valsight GmbH',
    role: 'UX/UI Designer',
    period: '09/2021 – 02/2022',
    startYear: 2021,
    endYear: 2022,
    description:
      'UX/UI-Design für die führende Szenario-Simulation Software für Financial Planning & Analysis (FP&A).',
    longDescription:
      'Gestaltung und Konzeption des UX/UI-Designs für Valsight – die schnellste Lösung zur KI-gestützten Szenario-Simulation für Finanz- und Strategie-Teams. Die Software ermöglicht treiberbasierte Szenarien in Sekunden statt wochenlanger Excel-Arbeit. Kunden wie BMW, Mercedes-Benz und EnBW nutzen die Plattform. Enge Zusammenarbeit mit dem Entwicklungsteam, iterative Prototypen und visuelle Optimierung komplexer Datenvisualisierungen.',
    highlights: [
      'KI-gestützte Szenario-Simulation',
      'FP&A Software für DAX-Konzerne',
      'Treiberbasierte Modelle',
      'Komplexe Datenvisualisierung',
    ],
    technologies: ['Figma', 'Adobe XD', 'HTML', 'CSS', 'JavaScript'],
    industry: 'FinTech & SaaS',
    type: 'freelance',
    current: false,
    location: 'Berlin / Remote',
  },
  {
    id: 'eon',
    company: 'E.ON Energie Deutschland GmbH',
    role: 'UX Designer',
    period: '10/2021 – 01/2022',
    startYear: 2021,
    endYear: 2022,
    description:
      'UX-Optimierung für Deutschlands größten Energieversorger mit 14 Millionen Kunden.',
    longDescription:
      'Konzeption und Optimierung von Userflows für E.ON – Deutschlands größten Energieversorger mit 14 Millionen Kunden. Überarbeitung von Header-Strukturen, Entwicklung neuer Solar-Features und Optimierung digitaler Produktberater. TÜV SÜD zertifiziert als "Wegbegleiter der Energiewende". Fokus auf Usability, Conversion und klare Nutzerführung.',
    highlights: [
      '14 Mio. Kunden Plattform',
      'Solar-Features entwickelt',
      'Conversion-Optimierung',
      'Digitale Produktberater',
    ],
    technologies: ['Figma', 'Sketch', 'Jira', 'Zeplin'],
    industry: 'Energie',
    type: 'freelance',
    current: false,
    location: 'Essen / Remote',
  },
  {
    id: 'argonauten',
    company: 'Argonauten GmbH',
    role: 'UX/UI Designer & Projektmanagement',
    period: '10/2021 – 01/2022',
    startYear: 2021,
    endYear: 2022,
    description:
      'UX/UI-Design für die digitale Full-Service-Agentur (Teil von WPP) mit Kunden wie Volkswagen.',
    longDescription:
      'Entwicklung nutzerzentrierter UX/UI-Konzepte für Argonauten – eine digitale Full-Service-Agentur als Teil des WPP-Netzwerks mit Standorten in Berlin, Düsseldorf, Hamburg, Frankfurt und München. Bekannt als Entwicklungspartner für VW "Das WeltAuto". Übersetzung von Kundenanforderungen in visuelle und funktionale Designs, Koordination von Projektabläufen und Übergabe dokumentierter Design-Assets.',
    highlights: [
      'Teil von WPP Netzwerk',
      'Volkswagen Projekte',
      'Digitale Strategieberatung',
      'Multi-Standort Agentur',
    ],
    technologies: ['Figma', 'Adobe XD'],
    industry: 'Digitalagentur',
    type: 'freelance',
    current: false,
    location: 'Hamburg / Remote',
  },
  {
    id: 'best-price',
    company: 'Best Price Germany GmbH',
    role: 'Head of Development & Design',
    period: '05/2018 – 03/2019 & 01/2020 – 01/2023',
    startYear: 2018,
    endYear: 2023,
    description:
      'Leitung Entwicklung & Design für internationalen Handelskonzern (Import/Export, Eigenmarken).',
    longDescription:
      'Leitung von Entwicklung und Design mehrerer internationaler E-Commerce-Plattformen für Best Price Germany – spezialisiert auf Einkauf in Fernost und Etablierung von Eigenmarken im Heim- und Haustextilbereich. Qualitätsmanagement durch international anerkannte Zertifizierungen. Verantwortung für Architekturentscheidungen, UX-Optimierung, Conversion-Steigerung, Teamführung und technische Skalierung.',
    highlights: [
      'Head of Development',
      'Internationale Handelsplattformen',
      'Eigenmarken-Entwicklung',
      'Teamführung & Skalierung',
    ],
    technologies: ['WordPress', 'WooCommerce', 'PHP', 'JavaScript', 'MySQL'],
    industry: 'Handel & E-Commerce',
    type: 'fulltime',
    current: false,
    location: 'Bonn / Remote',
  },
  {
    id: 'unitymedia',
    company: 'Unitymedia GmbH',
    role: 'UX/UI Designer',
    period: '01/2019 – 03/2019',
    startYear: 2019,
    endYear: 2019,
    description:
      'UX-Design für den Kabelnetzbetreiber (Hessen, NRW, BaWü) – heute Vodafone.',
    longDescription:
      'UX-Konzeption und visuelle Gestaltung der Speedtest-Website für Unitymedia – den damals größten Kabelnetzbetreiber für Hessen, NRW und Baden-Württemberg (2019 von Vodafone übernommen, Marke 2020 eingestellt). Durchführung von Prototyping, Usability-Tests und iterativer Optimierung in enger Zusammenarbeit mit Frontend-Entwicklungsteams.',
    highlights: [
      'Speedtest Website Design',
      'Millionen Endkunden',
      'Heute Teil von Vodafone',
      'Usability-Tests',
    ],
    technologies: ['Figma', 'Sketch', 'HTML', 'CSS'],
    industry: 'Telekommunikation',
    type: 'freelance',
    current: false,
    location: 'Köln',
  },
  {
    id: 'werbewunder',
    company: 'Werbewunder',
    role: 'Junior Art Director (UX/UI Schwerpunkt)',
    period: '06/2017 – 02/2018',
    startYear: 2017,
    endYear: 2018,
    description:
      'UX/UI-Design bei etabliertem Spezialisten für Textilveredelung und Werbetechnik (30+ Jahre).',
    longDescription:
      'Entwicklung kreativer UX/UI-Konzepte, Erstellung von Wireframes, Prototypen und Styleguides für Werbewunder – einem seit über 30 Jahren etablierten Spezialisten für hochwertige Stickereien und Drucklösungen in der Region Erftkreis/Frechen. Enge Abstimmung mit Entwicklungsteams.',
    highlights: [
      'UX/UI-Konzepte',
      'Wireframes & Prototypen',
      'Styleguides',
      '30+ Jahre Traditionsunternehmen',
    ],
    technologies: ['Sketch', 'Adobe Creative Cloud', 'InVision'],
    industry: 'Werbetechnik',
    type: 'fulltime',
    current: false,
    location: 'Frechen / Köln',
  },
  {
    id: 'lamers-media',
    company: 'Lamers.Media Service',
    role: 'UX/UI Designer & Web/App Entwickler',
    period: '06/2015 – 01/2017',
    startYear: 2015,
    endYear: 2017,
    description:
      'Gestaltung und Entwicklung bei Full-Service Werbeagentur (seit 2003).',
    longDescription:
      'Gestaltung und Entwicklung digitaler Anwendungen bei Lamers.Media – einer Full-Service Werbeagentur mit Schwerpunkten Webdesign, SEO und App-Entwicklung. Seit 2003 am Markt, ab 2012 auch in der Musikindustrie aktiv. Nutzerforschung, Prototyping, Frontend-Umsetzung und technische Integration in enger Zusammenarbeit mit interdisziplinären Teams.',
    highlights: [
      'Full-Service Werbeagentur',
      'Webdesign & SEO',
      'App-Entwicklung',
      'Erste Berufserfahrung',
    ],
    technologies: ['HTML', 'CSS', 'JavaScript', 'PHP', 'WordPress'],
    industry: 'Werbeagentur',
    type: 'fulltime',
    current: false,
    location: 'Köln',
  },
];

// Stats berechnet aus Daten
export const experienceStats = {
  totalYears: new Date().getFullYear() - 2015, // Seit 2015 in der Branche
  totalProjects: 50,
  currentPositions: experiencesExtended.filter((e) => e.current).length,
  industries: [...new Set(experiencesExtended.map((e) => e.industry))].length,
  technologies: [...new Set(experiencesExtended.flatMap((e) => e.technologies))].length,
};

// Gruppiert nach Jahr für Timeline
export function getExperiencesByYear(): Record<number, ExperienceExtended[]> {
  const grouped: Record<number, ExperienceExtended[]> = {};

  experiencesExtended.forEach((exp) => {
    const year = exp.startYear;
    if (!grouped[year]) {
      grouped[year] = [];
    }
    grouped[year]!.push(exp);
  });

  return grouped;
}

// Aktuelle Positionen
export function getCurrentExperiences(): ExperienceExtended[] {
  return experiencesExtended.filter((e) => e.current);
}

// Nach Typ filtern
export function getExperiencesByType(type: ExperienceExtended['type']): ExperienceExtended[] {
  return experiencesExtended.filter((e) => e.type === type);
}
